/**
 * mfLayer - 轻量级弹窗组件
 *
 * 使用：
 * mfLayer.open({
 *     title: '标题',
 *     content: '内容',
 *     area: ['800px', '600px'],
 *     position: 'center',
 *     className: 'my-layer', // 自定义类名(弹窗本体)
 *     modalClassName: 'my-modal', // 自定义类名(遮罩层)
 *     closeBtn: {
 *         pos: 'in-title', // 标题右侧
 *         pos: 'in-layer', // 或许你的弹窗没有标题，但又想显示关闭按钮
 *     },
 *     breakpoints: { // 你是不是需要在不同设备上展示不同弹窗位置？
 *         768: { // 768px 以下走这里
 *             area: ['100vw', '60vh'],
 *             position: 'bottom', // 底部展示
 *             closeBtn: {
 *                 pos: 'out-layer-bc', // 外部底部居中
 *                 class: 'my-close-btn', // 自定义类名
 *                 iconHtml: '<span style="font-size: 20px;">✕</span>' // 自定义关闭按钮
 *             }
 *         }
 *     },
 *     onOpen: function(instance) {
 *         console.log('弹窗打开', instance);
 *     },
 *     onClose: function() {
 *         console.log('弹窗关闭');
 *     }
 * });
 * 
 * 你还可以使用 mfLayer.tip 方法来显示提示信息：
 * mfLayer.tip('提示信息');
 * mfLayer.tip({ content: '提示信息', time: 5000 }); // 5秒后自动关闭
 * 
 * 你还可以使用 mfLayer.close 方法来关闭弹窗：
 * mfLayer.close(mask);
 *
 * @author luochongfei
 * @version 2.0.1
 */
(function (window, document) {
    const mfLayer = {
        // 默认配置
        defaults: {
            type: 1, // 1: 内容弹窗, 2: iframe弹窗, 3: tip提示
            title: 'title',
            content: '',
            url: '',
            className: '',
            area: ['1100px', '620px'], // 默认尺寸
            position: 'center', // 定位方式: center, bottom, top, left, right
            modal: true,
            background: 'rgba(0, 0, 0, 0.5)',
            closeBtn: true,
            shadeClose: true,
            onOpen: null, // 打开回调函数
            onClose: null,
            style: '',
            zIndex: 9999,
            
            // 响应式断点配置
            breakpoints: {},
            
            // 向后兼容的配置（会自动转换为 breakpoints）
            desktopArea: null,
            mobileArea: null
        },

        // 默认断点
        defaultBreakpoints: {
            768: {} // 移动端断点
        },

        // 弹窗计数器
        modalCount: 0,

        // 存储当前打开的弹窗实例
        activeModals: [],

        // 窗口大小变化监听器
        resizeHandler: null,

        // 基础 z-index
        baseZIndex: 9999,

        // ESC 键监听器（全局唯一）
        escHandler: null,

        /**
         * 获取当前断点配置
         * @param {Object} config - 配置对象
         * @returns {Object} 当前断点的配置
         */
        getCurrentBreakpointConfig: function(config) {
            const width = window.innerWidth;
            let currentConfig = { 
                area: config.area, 
                position: config.position,
                closeBtn: config.closeBtn
            };

            // 处理向后兼容的 mobile/desktop 配置
            const breakpoints = this.normalizeBreakpoints(config);

            // 按断点从小到大排序
            const sortedBreakpoints = Object.keys(breakpoints)
                .map(Number)
                .sort((a, b) => a - b);

            // 找到适用的断点配置
            for (let breakpoint of sortedBreakpoints) {
                if (width <= breakpoint) {
                    Object.assign(currentConfig, breakpoints[breakpoint]);
                    break;
                }
            }

            return currentConfig;
        },

        /**
         * 标准化断点配置（处理向后兼容）
         * @param {Object} config - 配置对象
         * @returns {Object} 标准化后的断点配置
         */
        normalizeBreakpoints: function(config) {
            const breakpoints = Object.assign({}, config.breakpoints);

            // 处理向后兼容的 mobileArea 配置
            if (config.mobileArea) {
                (breakpoints[768] = breakpoints[768] || {}).area = config.mobileArea;
            }

            return breakpoints;
        },

        /**
         * 初始化响应式监听
         */
        initResponsive: function() {
            if (this.resizeHandler) return;

            this.resizeHandler = () => {
                this.activeModals.forEach(modal => {
                    this.updateModal(modal);
                });
            };

            window.addEventListener('resize', this.resizeHandler);
        },

        /**
         * 更新弹窗（响应式）
         * @param {Object} modal - 弹窗实例
         */
        updateModal: function(modal) {
            if (!modal || !modal.layer || !modal.config) return;

            const bpConfig = this.getCurrentBreakpointConfig(modal.config);
            const width = window.innerWidth;
            const isMobile = width <= 768;

            // 更新尺寸
            modal.layer.style.width = bpConfig.area[0];
            modal.layer.style.height = bpConfig.area[1];

            // 更新定位类名
            this.updatePositionClass(modal.layer, bpConfig.position, isMobile);

            // 更新 transform（根据定位类型）
            this.applyPositionTransform(modal.layer, bpConfig.position, true);

            // 更新关闭按钮（如果断点配置了不同的 closeBtn）
            this.updateCloseButton(modal.layer, modal.config, bpConfig);

            // 更新自定义样式
            if (modal.config.style) {
                const content = modal.layer.querySelector('.mf-layer-content');
                if (content) {
                    content.style.cssText += modal.config.style;
                }
            }
        },

        /**
         * 更新定位类名
         * @param {Element} layer - 弹窗元素
         * @param {string} position - 定位方式
         * @param {boolean} isMobile - 是否移动端
         */
        updatePositionClass: function(layer, position, isMobile) {
            const positionClasses = ['center', 'bottom', 'top', 'left', 'right'];
            
            // 移除旧的定位类名
            layer.classList.remove('mf-layer-mobile', 'mf-layer-desktop', 
                ...positionClasses.map(p => `mf-layer-position-${p}`));
            
            // 添加新的类名
            layer.classList.add(isMobile ? 'mf-layer-mobile' : 'mf-layer-desktop', 
                `mf-layer-position-${position}`);
        },

        /**
         * 应用定位 transform
         * @param {Element} element - 元素
         * @param {string} position - 定位方式
         * @param {boolean} isShow - 是否为显示状态
         */
        applyPositionTransform: function(element, position, isShow = true) {
            const transforms = {
                'center': isShow ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0.8)',
                'bottom': isShow ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(100%)',
                'top': isShow ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(-100%)',
                'left': isShow ? 'translateY(-50%) translateX(0)' : 'translateY(-50%) translateX(-100%)',
                'right': isShow ? 'translateY(-50%) translateX(0)' : 'translateY(-50%) translateX(100%)'
            };

            element.style.transform = transforms[position] || transforms['center'];
        },

        /**
         * 更新关闭按钮（响应式断点切换）
         * @param {Element} layer - 弹窗元素
         * @param {Object} config - 原始配置
         * @param {Object} bpConfig - 当前断点配置
         */
        updateCloseButton: function(layer, config, bpConfig) {
            // 移除所有现有的关闭按钮
            layer.querySelectorAll('.mf-layer-close-btn').forEach(btn => btn.remove());

            // 获取或创建 header（如果有标题的话）
            if (config.title && !layer.querySelector('.mf-layer-header')) {
                this.createHeader(layer, config);
            }

            // 如果当前断点配置了 closeBtn，重新添加
            if (bpConfig.closeBtn) {
                const closeBtnConfig = this.parseCloseBtnConfig(bpConfig.closeBtn);
                this.addCloseBtnByPosition(layer, config, closeBtnConfig, true);
            }
        },

        /**
         * 禁止页面滚动
         */
        disableBodyScroll: function() {
            const scrollY = window.scrollY;
            document.body.style.paddingRight = window.innerWidth - document.documentElement.clientWidth + 'px';
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
        },

        /**
         * 恢复页面滚动
         */
        enableBodyScroll: function() {
            const scrollY = document.body.style.top;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.paddingRight = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
            if (scrollY) {
                window.scrollTo({
                    top: parseInt(scrollY || '0') * -1,
                    left: 0,
                    behavior: 'instant'
                });
            }
        },

        /**
         * 打开弹窗/提示
         * @param {Object} options - 配置选项
         * @param {Function} options.onOpen - 打开回调函数，弹窗打开动画完成后调用，接收实例作为参数
         * @param {Function} options.onClose - 关闭回调函数，弹窗关闭时调用
         * @returns {Object} 实例 {close: function, mask: Element, layer: Element}
         */
        open: function(options = {}) {
            // 合并配置
            const config = Object.assign({}, this.defaults, options);

            // 处理向后兼容的 desktopArea 配置（优先级高于 defaults）
            if (options.desktopArea && !options.area) {
                config.area = options.desktopArea;
            }

            // 对于 tip 类型，设置特殊默认值
            if (config.type === 3) {
                config.modal = false;
                config.closeBtn = false;
                config.shadeClose = false;
                config.background = 'transparent';
                if (!config.className) config.className = 'mf-layer-tip';
            }

            // 初始化响应式监听
            if (config.type !== 3) {
                this.initResponsive();
            }

            // 处理模态弹窗的滚动
            if (config.modal) {
                this.modalCount++;
                if (this.modalCount === 1) {
                    this.disableBodyScroll();
                }
            }

            // 计算当前弹窗的 z-index（每个弹窗递增10，确保足够的层级空间）
            const currentZIndex = this.baseZIndex + (this.activeModals.length * 10);
            config.zIndex = currentZIndex;

            // 创建遮罩层
            const mask = this.createMask(config);

            // 创建弹窗容器
            const layer = this.createLayer(config);

            // 添加到页面（必须在 addContent 之前，因为 addContent 需要 layer.parentNode）
            document.body.appendChild(mask);
            mask.appendChild(layer);

            // 添加内容（包括关闭按钮）
            this.addContent(layer, config);

            // 显示动画
            setTimeout(() => {
                mask.style.opacity = '1';
                const bpConfig = this.getCurrentBreakpointConfig(config);
                this.applyPositionTransform(layer, bpConfig.position, true);
                
                // 动画完成后调用 onOpen 回调
                setTimeout(() => {
                    if (config.onOpen && typeof config.onOpen === 'function') {
                        config.onOpen(instance);
                    }
                }, 300); // 等待动画完成（0.3s）
            }, 10);

            // 绑定事件
            this.bindEvents(mask, layer, config);

            // 创建实例
            const instance = {
                close: () => this.close(mask),
                mask: mask,
                layer: layer,
                config: config
            };

            // 添加到活跃列表（tip 类型不需要）
            if (config.type !== 3) {
                this.activeModals.push(instance);
            }

            return instance;
        },

        /**
         * 通用元素创建
         */
        createElement: function(tagName, className, styles = {}, attributes = {}) {
            const element = document.createElement(tagName);
            if (className) element.className = className;
            Object.assign(element.style, styles);
            Object.assign(element, attributes);
            return element;
        },

        /**
         * 创建遮罩层
         */
        createMask: function(config) {
            return this.createElement('div', `mf-layer-mask ${config.modalClassName || ''}`, {
                background: config.background,
                zIndex: config.zIndex,
                opacity: '0',
                transition: 'opacity 0.3s ease'
            });
        },

        /**
         * 创建弹窗容器
         */
        createLayer: function(config) {
            const width = window.innerWidth;
            const isMobile = width <= 768;
            const bpConfig = this.getCurrentBreakpointConfig(config);
            
            // 构建类名
            const classes = ['mf-layer'];
            classes.push(`mf-layer-position-${bpConfig.position}`);
            classes.push(isMobile ? 'mf-layer-mobile' : 'mf-layer-desktop');
            if (config.className) {
                classes.push(config.className);
            }

            const styles = {
                width: bpConfig.area[0],
                height: bpConfig.area[1],
                transition: 'transform 0.3s ease'
            };

            // tip 类型需要透明背景和移除阴影
            if (config.type === 3) {
                styles.background = 'transparent';
                styles.boxShadow = 'none';
            }

            const layer = this.createElement('div', classes.join(' '), styles);

            // 设置初始 transform（隐藏状态）
            this.applyPositionTransform(layer, bpConfig.position, false);

            return layer;
        },

        /**
         * 添加内容
         */
        addContent: function(layer, config) {
            // 获取当前断点配置
            const bpConfig = this.getCurrentBreakpointConfig(config);
            
            // 如果有标题，先创建 header
            if (config.title) {
                this.createHeader(layer, config);
            }
            
            // 处理关闭按钮（使用断点配置的 closeBtn）
            if (bpConfig.closeBtn) {
                const closeBtnConfig = this.parseCloseBtnConfig(bpConfig.closeBtn);
                this.handleCloseButton(layer, config, closeBtnConfig);
            }

            // 创建内容区域
            this.createContentArea(layer, config);
        },

        /**
         * 处理关闭按钮
         */
        handleCloseButton: function(layer, config, closeBtnConfig) {
            this.addCloseBtnByPosition(layer, config, closeBtnConfig, false);
        },

        /**
         * 根据位置添加关闭按钮
         * @param {Element} layer - 弹窗元素
         * @param {Object} config - 配置对象
         * @param {Object} closeBtnConfig - 关闭按钮配置
         * @param {Boolean} isUpdate - 是否为更新模式（使用 insertBefore）
         */
        addCloseBtnByPosition: function(layer, config, closeBtnConfig, isUpdate) {
            const mask = layer.parentNode;

            if (closeBtnConfig.pos === 'in-title') {
                if (!config.title) {
                    // 无标题时，关闭按钮内联到弹窗容器
                    const closeBtn = this.createCloseButton(closeBtnConfig, mask);
                    closeBtn.className += ' mf-layer-close-in-layer';
                    isUpdate ? layer.insertBefore(closeBtn, layer.firstChild) : layer.appendChild(closeBtn);
                } else {
                    // 有标题时添加到头部
                    const header = layer.querySelector('.mf-layer-header');
                    if (header) {
                        header.appendChild(this.createCloseButton(closeBtnConfig, mask));
                    }
                }
            } else if (closeBtnConfig.pos === 'in-layer') {
                // 弹窗内部显示关闭按钮
                const closeBtn = this.createCloseButton(closeBtnConfig, mask);
                closeBtn.className += ' mf-layer-close-in-layer';
                isUpdate ? layer.insertBefore(closeBtn, layer.firstChild) : layer.appendChild(closeBtn);
            } else if (closeBtnConfig.pos === 'out-layer-tr') {
                // 外部顶部右侧
                const closeBtn = this.createCloseButton(closeBtnConfig, mask);
                closeBtn.className += ' mf-layer-close-external-top';
                layer.appendChild(closeBtn);
            } else if (closeBtnConfig.pos === 'out-layer-bc') {
                // 外部底部居中
                const closeBtn = this.createCloseButton(closeBtnConfig, mask);
                closeBtn.className += ' mf-layer-close-external-bottom';
                layer.appendChild(closeBtn);
            }
        },

        /**
         * 创建头部
         */
        createHeader: function(layer, config) {
            const header = this.createElement('div', 'mf-layer-header');
            const title = this.createElement('div', 'mf-layer-title');
            title.innerHTML = config.title;
            header.appendChild(title);
            // 插入到第一个位置
            layer.insertBefore(header, layer.firstChild);
        },

        /**
         * 创建内容区域
         */
        createContentArea: function(layer, config) {
            const content = this.createElement('div', 'mf-layer-content', {
                overflow: config.type === 3 ? 'visible' : 'auto',
                overflowX: 'hidden',
                scrollBehavior: 'smooth'
            });

            if (config.style) {
                content.style.cssText += config.style;
            }

            if (config.type === 2 && config.url) {
                const iframe = this.createElement('iframe', '', {}, { src: config.url });
                content.appendChild(iframe);
            } else {
                content.innerHTML = config.content;
            }

            layer.appendChild(content);
        },

        /**
         * 解析关闭按钮配置
         */
        parseCloseBtnConfig: function(closeBtn) {
            if (!closeBtn) return { enabled: false };
            
            if (typeof closeBtn === 'boolean') {
                return { 
                    enabled: true, 
                    pos: 'in-title',
                    class: '',
                    iconHtml: null
                };
            }
            
            if (typeof closeBtn === 'object') {
                // 标准化 pos 值（兼容旧版数字配置）
                let pos = closeBtn.pos || 'in-title';
                
                // 向后兼容：数字转字符串
                const posMap = {
                    1: 'in-title',
                    2: 'out-layer-tr',
                    3: 'out-layer-bc'
                };
                
                if (typeof pos === 'number' && posMap[pos]) {
                    pos = posMap[pos];
                }
                
                return {
                    enabled: true,
                    pos: pos,
                    class: closeBtn.class || '',
                    iconHtml: closeBtn.iconHtml || null
                };
            }
            
            return { enabled: false };
        },

        /**
         * 创建关闭按钮
         */
        createCloseButton: function(config, mask) {
            const baseClass = 'mf-layer-close-btn';
            const classes = [baseClass];
            
            // 如果没有自定义图标HTML，使用默认图标类
            if (!config.iconHtml) {
                classes.push('mf-layer-close-icon');
            }
            
            // 添加自定义类名
            if (config.class) {
                classes.push(config.class);
            }
            
            const closeBtn = this.createElement('a', classes.join(' '));
            
            // 如果有自定义图标HTML，使用它
            if (config.iconHtml) {
                closeBtn.innerHTML = config.iconHtml;
            }
            
            closeBtn.addEventListener('click', (e) => {
                e.stopPropagation(); // 阻止事件冒泡
                this.close(mask);
            });
            return closeBtn;
        },

        /**
         * 绑定事件
         */
        bindEvents: function(mask, layer, config) {
            // 点击遮罩关闭（只关闭最顶层弹窗）
            if (config.shadeClose) {
                mask.addEventListener('click', (e) => {
                    if (e.target === mask && this.activeModals.length > 0) {
                        // 检查当前 mask 是否是最顶层的弹窗
                        const topModal = this.activeModals[this.activeModals.length - 1];
                        if (topModal && topModal.mask === mask) {
                            this.close(mask);
                        }
                    }
                });
            }

            // 内容区域的关闭按钮
            const content = layer.querySelector('.mf-layer-content');
            if (content) {
                content.addEventListener('click', (e) => {
                    if (e.target.classList.contains('mf-layer-close') || e.target.closest('.mf-layer-close')) {
                        e.stopPropagation(); // 阻止事件冒泡
                        this.close(mask);
                    }
                });
            }

            // 初始化全局 ESC 键监听器（只在第一个弹窗打开时绑定）
            if (!this.escHandler) {
                this.escHandler = (e) => {
                    if (e.key === 'Escape' && this.activeModals.length > 0) {
                        // 只关闭最顶层的弹窗（数组最后一个）
                        const topModal = this.activeModals[this.activeModals.length - 1];
                        if (topModal && topModal.mask) {
                            this.close(topModal.mask);
                        }
                    }
                };
                document.addEventListener('keydown', this.escHandler);
            }
        },

        /**
         * 关闭弹窗
         */
        close: function(mask) {
            // 智能查找 mask
            if (!mask || !mask.classList.contains('mf-layer-mask')) {
                let currentElement = mask;
                while (currentElement && !currentElement.classList.contains('mf-layer-mask')) {
                    currentElement = currentElement.parentNode;
                }
                mask = currentElement || document.querySelector('.mf-layer-mask');
            }

            if (!mask) {
                console.warn('mfLayer: Mask not found');
                return;
            }

            const layer = mask.querySelector('.mf-layer');
            const modalInstance = this.activeModals.find(modal => modal.mask === mask);
            const config = modalInstance ? modalInstance.config : null;

            // 从活跃列表中移除
            this.activeModals = this.activeModals.filter(modal => modal.mask !== mask);

            // 处理模态弹窗计数
            if (config && config.modal) {
                this.modalCount = Math.max(0, this.modalCount - 1);
                if (this.modalCount === 0) {
                    this.enableBodyScroll();
                }
            }

            // 当所有弹窗都关闭后，清理全局监听器
            if (this.activeModals.length === 0) {
                if (this.resizeHandler) {
                    window.removeEventListener('resize', this.resizeHandler);
                    this.resizeHandler = null;
                }
                if (this.escHandler) {
                    document.removeEventListener('keydown', this.escHandler);
                    this.escHandler = null;
                }
            }

            // 关闭动画
            const position = config ? this.getCurrentBreakpointConfig(config).position : 'center';
            this.applyPositionTransform(layer, position, false);
            mask.style.opacity = '0';

            setTimeout(() => {
                if (mask.parentNode) {
                    mask.parentNode.removeChild(mask);
                }
                if (config && config.onClose && typeof config.onClose === 'function') {
                    config.onClose();
                }
            }, 300);
        },

        /**
         * 显示提示信息（使用 open 方法实现）
         * @param {Object|string} options - 配置选项或提示内容
         * @returns {Object} tip实例
         */
        tip: function(options) {
            // 字符串转配置对象
            if (typeof options === 'string') {
                options = { content: options };
            }

            // tip 默认配置
            const tipConfig = Object.assign({
                type: 3,
                title: false,
                content: '',
                area: ['auto', 'auto'],
                position: 'center',
                className: 'mf-layer-tip-message',
                style: 'padding: 10px 15px; background: rgba(0, 0, 0, 0.8); color: #fff; font-size: 14px; border-radius: 4px; max-width: 300px; text-align: center;',
                time: 3000,
                modal: false,
                closeBtn: false,
                shadeClose: false,
                background: 'transparent',
                // 支持不同位置的 tip
                breakpoints: {}
            }, options);

            // 根据 position 参数添加对应的类名
            if (options.position === 'top' || options.position === 'bottom') {
                tipConfig.className += ` mf-layer-tip-${options.position}`;
            }

            // 调用 open 方法
            const instance = this.open(tipConfig);

            // 自动关闭
            if (tipConfig.time > 0) {
                setTimeout(() => {
                    instance.close();
                }, tipConfig.time);
            }

            return instance;
        },

        /**
         * 显示加载提示
         * @param {Object|string} options - 配置选项或提示文字
         * @returns {Object} loading实例
         */
        loading: function(options) {
            if (typeof options === 'string') {
                options = { text: options };
            }

            return this.open(Object.assign({
                type: 4,
                title: false,
                area: ['auto', 'auto'],
                content: '<div class="mf-loading-spinner"></div>' + (options?.text ? `<div class="mf-loading-text">${options.text}</div>` : ''),
                className: 'mf-layer-loading',
                modal: true,
                closeBtn: false,
                shadeClose: false
            }, options));
        },
    };

    window.mfLayer = mfLayer;
})(window, document);
